import * as React from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getGlobalSubscribeUrl } from '../../../modules/router.utils';
import { applyPromotionCode } from '../../../modules/Subscription';
import InlineLoader from '../InlineLoader';
import Button from '../Button/Button';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Input from '../Input/Input';
import cn from '~modules/cn';

import './payment-form.scss';

type Props = {
    tierId: string;
    projectId: string;
    subscriptionId: string;
    onPaymentStart?: () => void;
    onPaymentSuccess?: () => void;
    onPaymentFailure?: (message: string) => void;
    onPromotionCodeUpdate?: (status: 'start' | 'success' | 'fail', amount?: number) => void;
};

const PaymentForm = (props: Props) => {
    const stripe = useStripe();
    const elements = useElements();

    const [isLoading, setIsLoading] = React.useState(true);
    const [isSuccess, setIsSuccess] = React.useState(false);
    const [isLoadingPromo, setLoadingPromo] = React.useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [promotionCode, setPromotionCode] = React.useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!stripe || !elements || isLoadingPromo) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setIsLoading(true);

        props.onPaymentStart?.();

        const returnUrl = new URL(getGlobalSubscribeUrl(props.projectId, props.tierId, true));

        const result = await stripe.confirmPayment({
            elements,
            // both studio and subscription page uses payment component
            // if in some case we need to be redirected to e.g. 3ds page or something - we can't return to the studio, we need to return to
            // the subscription page
            confirmParams: {
                return_url: returnUrl.toString()
            },
            redirect: 'if_required'
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (result.error) {
            if (result.error?.type === 'card_error' || result.error?.type === 'validation_error') {
                props.onPaymentFailure?.(result.error.message ?? '');
            } else {
                props.onPaymentFailure?.('An unexpected error occurred');
            }
        } else if (result.paymentIntent?.status === 'succeeded') {
            // payment went through
            setIsSuccess(true);
            props.onPaymentSuccess?.();
        } else {
            props.onPaymentFailure?.('An unexpected error occurred');
        }

        setIsLoading(false);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const applyCoupon = async () => {
        if (!promotionCode) {
            return;
        }
        setLoadingPromo(true);
        try {
            props.onPromotionCodeUpdate?.('start');
            const result = await applyPromotionCode({
                projectId: props.projectId,
                subscriptionId: props.subscriptionId,
                promotionCode
            });
            // @ts-expect-error TS(2571): Object is of type 'unknown'.
            props.onPromotionCodeUpdate?.(result.success ? 'success' : 'fail', result.amount);
        } catch (e) {
            props.onPromotionCodeUpdate?.('fail');
        } finally {
            setLoadingPromo(false);
        }
    };

    return (
        <form className={cn('payment-form', { 'payment-form--loading': isLoading })} id="payment-form" onSubmit={handleSubmit}>
            <PaymentElement id="payment-element" options={{ readOnly: isLoading }} onReady={() => setIsLoading(false)} />
            {!isLoading && !isSuccess && (
                <>
                    {/* <div className="payment-form__block">
                        <div className="payment-form__block-title">Promotion code</div>
                        <div className="payment-form__coupon-block">
                            <Input
                                value={promotionCode}
                                onChange={(e) => setPromotionCode(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        applyCoupon();
                                        e.preventDefault();
                                    }
                                }}
                                placeholder="Code"
                            />
                            <Button buttonType="secondary" buttonSize="small" type="button" onClick={applyCoupon}>
                                Apply code
                            </Button>
                        </div>
                    </div> */}
                    <Button buttonType="primary" disabled={!stripe || !elements} className="pay-button" id="submit">
                        Pay & Upgrade
                    </Button>
                </>
            )}
            {isLoading && <InlineLoader color="multipass" />}
        </form>
    );
};

export default PaymentForm;
